// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web\src\pages\RentalsPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.7 – Padronização Visual (Light Mode).
 * Impacto da Versão: [src/pages/RentalsPage.js]
 * Data: 01/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - MODIFICADO: Substituição total de classes de estilo Dark Mode por Light Mode (bg-gray-50/bg-white).
 * - MODIFICADO: Estilização dos inputs de filtro e busca para fundo branco e bordas suaves.
 * - MODIFICADO: Adaptação completa do Modal de Detalhes para o tema claro.
 * - MANTIDO: Toda a lógica de fetch, tratamento de datas e integração com Firebase.
 */
//
import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase/config';
import { collection, getDocs, query, orderBy, where, Timestamp } from 'firebase/firestore'; 
import { onAuthStateChanged } from 'firebase/auth';
import { Loader2, Search, PlusCircle, CalendarDays, User, Package } from 'lucide-react';
import AddRentalForm from '../components/forms/AddRentalForm';
import Modal from '../components/ui/Modal'; 

const RentalsPage = () => {
    const [rentals, setRentals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRentalForDetails, setSelectedRentalForDetails] = useState(null); 

    const [isAddRentalModalOpen, setIsAddRentalModalOpen] = useState(false); 
    
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setLoggedInUser({ uid: user.uid, name: user.displayName || user.email });
            } else {
                setLoggedInUser(null);
            }
        });
        return () => unsubscribe();
    }, []);

    const fetchRentals = useCallback(async () => {
        if (!loggedInUser) return;
        setIsLoading(true);
        try {
            let rentalsQuery = collection(db, 'rentals');

            if (filterStatus !== 'all') {
                rentalsQuery = query(rentalsQuery, where('status', '==', filterStatus));
            }
            
            rentalsQuery = query(rentalsQuery, orderBy('createdAt', 'desc'));

            const querySnapshot = await getDocs(rentalsQuery);
            const fetchedRentals = querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    rentalStartDate: data.rentalStartDate instanceof Timestamp ? data.rentalStartDate.toDate() : (data.rentalStartDate ? new Date(data.rentalStartDate) : null), 
                    rentalEndDate: data.rentalEndDate instanceof Timestamp ? data.rentalEndDate.toDate() : (data.rentalEndDate ? new Date(data.rentalEndDate) : null),
                    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : null),
                };
            });

            const filteredBySearch = searchTerm
                ? fetchedRentals.filter(rental => {
                    const customerMatch = rental.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
                    const piecesMatch = rental.pieces?.some(piece => 
                        piece.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        piece.sku?.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                    return customerMatch || piecesMatch;
                  })
                : fetchedRentals;

            setRentals(filteredBySearch);

        } catch (error) {
            console.error("Erro ao carregar aluguéis:", error);
        } finally {
            setIsLoading(false);
        }
    }, [loggedInUser, filterStatus, searchTerm]);

    const openAddRentalModal = useCallback(() => {
        setIsAddRentalModalOpen(true);
    }, []);

    const closeAddRentalModal = useCallback(() => {
        setIsAddRentalModalOpen(false);
        fetchRentals(); 
    }, [fetchRentals]);

    const openDetailsModal = useCallback((rental) => {
        setSelectedRentalForDetails(rental);
    }, []);

    const closeDetailsModal = useCallback(() => {
        setSelectedRentalForDetails(null);
    }, []);


    useEffect(() => {
        if (loggedInUser) {
            fetchRentals();
        }
    }, [loggedInUser, fetchRentals]);

    const handleViewDetails = (rental) => {
        openDetailsModal(rental);
    };

    const handleAddRental = () => {
        openAddRentalModal();
    };

    const formatCurrency = (value) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const formatDate = (date) => date?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) || 'N/A';
    const formatDateTime = (date) => date?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) || 'N/A';


    if (!loggedInUser || isLoading) {
        return <div className="flex h-screen w-full items-center justify-center bg-gray-50"><Loader2 className="h-12 w-12 animate-spin text-brand-gold" /></div>;
    }

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans text-gray-800">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <CalendarDays className="text-brand-gold" size={28}/>
                        Gerenciamento de Aluguéis
                    </h1>
                    <p className="mt-1 text-gray-500">Visualize, filtre e registre todas as peças alugadas e reservadas.</p>
                </div>
            </div>

            {/* Filtros e Ações */}
            <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 mb-8">
                <div className="flex flex-col md:flex-row justify-between items-end gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow w-full md:w-auto">
                        <div>
                            <label htmlFor="filterStatus" className="block text-sm font-medium text-gray-600 mb-2">Filtrar por Status</label>
                            <select
                                id="filterStatus"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="w-full p-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent text-gray-900 shadow-sm"
                            >
                                <option value="all">Todos</option>
                                <option value="reserved">Reservado</option>
                                <option value="rented">Em Uso (Alugado)</option>
                                <option value="returned_ok">Devolvido (OK)</option>
                                <option value="overdue">Em Atraso</option>
                                <option value="returned_damaged">Devolvido (Dano)</option>
                                <option value="cancelled">Cancelado</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-600 mb-2">Buscar (Cliente/Peça)</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    id="searchTerm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar por nome ou SKU..."
                                    className="w-full p-2.5 pl-10 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent text-gray-900 shadow-sm"
                                />
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleAddRental}
                        className="bg-brand-gold text-brand-black font-semibold py-2.5 px-6 rounded-lg shadow-md hover:bg-yellow-500 transition duration-200 flex items-center justify-center w-full md:w-auto h-[42px]"
                    >
                        <PlusCircle className="w-5 h-5 mr-2" />
                        Novo Aluguel
                    </button>
                </div>
            </div>

            {/* Tabela */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                {rentals.length === 0 ? (
                    <div className="p-10 text-center">
                        <p className="text-gray-400 text-lg">Nenhum registro de aluguel encontrado para os filtros selecionados.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Data Início</th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cliente</th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peças</th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tipo/Plano</th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                    <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Caução</th>
                                    <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {rentals.map((rental) => (
                                    <tr key={rental.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(rental.rentalStartDate)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-2">
                                            <User size={16} className="text-gray-400"/>
                                            {rental.customerName}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {rental.pieces && rental.pieces.length > 0 ? (
                                                <div className="flex items-center gap-1">
                                                    <Package size={16} className="text-gray-400"/>
                                                    <span>{rental.pieces.length} peça(s)</span>
                                                </div>
                                            ) : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                            {rental.rentalType === 'plan' ? `Plano ${rental.planName}` : 'Avulso'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full border 
                                                ${rental.status === 'rented' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}
                                                ${rental.status === 'reserved' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : ''}
                                                ${rental.status === 'returned_ok' ? 'bg-green-50 text-green-700 border-green-200' : ''}
                                                ${rental.status === 'overdue' || rental.status === 'returned_damaged' ? 'bg-red-50 text-red-700 border-red-200' : ''}
                                                ${rental.status === 'cancelled' ? 'bg-gray-100 text-gray-600 border-gray-200' : ''}
                                            `}>
                                                {rental.status.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700">
                                            {formatCurrency(rental.caucaoAmount || 0)}
                                            <div className="text-xs text-gray-400">{rental.caucaoPaymentMethod?.replace(/_/g, ' ') || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button 
                                                onClick={() => handleViewDetails(rental)}
                                                className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                                            >
                                                Detalhes
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal para Adicionar Novo Aluguel */}
            {isAddRentalModalOpen && (
                <Modal onClose={closeAddRentalModal} size="xl" title="Registrar Novo Aluguel">
                    <AddRentalForm
                        onFormSubmit={closeAddRentalModal}
                        onCancel={closeAddRentalModal}
                    />
                </Modal>
            )}

            {/* Modal para Detalhes do Aluguel */}
            {selectedRentalForDetails && (
                <Modal onClose={closeDetailsModal} size="lg" title="Detalhes do Aluguel">
                    <div className="p-4 text-gray-800">
                        <div className="flex justify-between items-start border-b border-gray-200 pb-4 mb-4">
                            <h3 className="text-lg font-bold text-gray-900">Aluguel #{selectedRentalForDetails.id.substring(0, 6)}</h3>
                            <span className={`px-3 py-1 text-sm font-semibold rounded-full border
                                ${selectedRentalForDetails.status === 'rented' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}
                                ${selectedRentalForDetails.status === 'reserved' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : ''}
                                ${selectedRentalForDetails.status === 'returned_ok' ? 'bg-green-50 text-green-700 border-green-200' : ''}
                                ${selectedRentalForDetails.status === 'overdue' || selectedRentalForDetails.status === 'returned_damaged' ? 'bg-red-50 text-red-700 border-red-200' : ''}
                                ${selectedRentalForDetails.status === 'cancelled' ? 'bg-gray-100 text-gray-600 border-gray-200' : ''}
                            `}>
                                {selectedRentalForDetails.status.replace(/_/g, ' ')}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                            <div>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Cliente:</span> <span className="text-gray-900">{selectedRentalForDetails.customerName}</span></p>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Tipo:</span> {selectedRentalForDetails.rentalType === 'plan' ? `Plano ${selectedRentalForDetails.planName}` : 'Avulso'}</p>
                                {selectedRentalForDetails.rentalType === 'aspirant' && (
                                    <p className="mb-2"><span className="font-semibold text-gray-600">Taxa:</span> {formatCurrency(selectedRentalForDetails.rentalFee || 0)}</p>
                                )}
                                <p className="mb-2"><span className="font-semibold text-gray-600">Início:</span> {formatDate(selectedRentalForDetails.rentalStartDate)}</p>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Fim Previsto:</span> {formatDate(selectedRentalForDetails.rentalEndDate)}</p>
                            </div>
                            <div>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Caução:</span> {formatCurrency(selectedRentalForDetails.caucaoAmount || 0)}</p>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Método:</span> {selectedRentalForDetails.caucaoPaymentMethod?.replace(/_/g, ' ') || 'N/A'}</p>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Registrado por:</span> {selectedRentalForDetails.recordedBy?.name || 'N/A'}</p>
                                <p className="mb-2"><span className="font-semibold text-gray-600">Data Registro:</span> {formatDateTime(selectedRentalForDetails.createdAt)}</p>
                            </div>
                        </div>

                        <div className="mt-6">
                            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 border-b border-gray-100 pb-1">Peças Alugadas</h4>
                            {selectedRentalForDetails.pieces && selectedRentalForDetails.pieces.length > 0 ? (
                                <ul className="space-y-2">
                                    {selectedRentalForDetails.pieces.map((piece, index) => (
                                        <li key={index} className="bg-gray-50 border border-gray-100 p-3 rounded-md flex justify-between items-center">
                                            <div>
                                                <p className="font-semibold text-gray-800">{piece.name}</p>
                                                <p className="text-xs text-gray-500 font-mono">SKU: {piece.sku}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-gray-600 font-medium">{formatCurrency(piece.price || 0)}</p>
                                                {piece.rentalGroups && piece.rentalGroups.length > 0 && (
                                                    <p className="text-xs text-gray-400">{piece.rentalGroups.join(', ')}</p>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-400 italic text-sm">Nenhuma peça registrada.</p>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default RentalsPage;